import { apiClient } from "./client";
import type {
  Adresse,
  AdresseListItem,
  AuditLogEntry,
  Haus,
  Einheit,
  HierarchyResponse,
} from "../types";
import type { PaginatedResponse } from "../types/api";

export interface AdressenQueryParams {
  page?: number;
  per_page?: number;
  search_text?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  include_hidden?: boolean;
}

export type AdresseListResponse = PaginatedResponse<AdresseListItem>;

export interface AdresseDetail extends Adresse {
  user_id: number;
  bplan_nr: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  haeuser: (Haus & { einheiten_count: number })[];
}

export interface AdresseUpdateData {
  strasse?: string;
  hausnummer?: string;
  plz?: string | null;
  stadt?: string | null;
  bplan_nr?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  notes?: string | null;
}

export interface HierarchyCreateData {
  strasse: string;
  hausnummer: string;
  plz?: string;
  stadt?: string;
  haus_bezeichnung?: string;
  einheit_bezeichnung?: string;
  einheit_typ?: string;
}

export const adressenApi = {
  getAdressen: async (
    params: AdressenQueryParams = {},
  ): Promise<AdresseListResponse> => {
    const { data } = await apiClient.get("/adressen/", { params });
    return data;
  },

  search: async (q: string): Promise<Adresse[]> => {
    const { data } = await apiClient.get("/adressen/suche", { params: { q } });
    return data;
  },

  getAdresse: async (id: number): Promise<AdresseDetail> => {
    const { data } = await apiClient.get(`/adressen/${id}`);
    return data;
  },

  updateAdresse: async (
    id: number,
    updates: AdresseUpdateData,
  ): Promise<Adresse> => {
    const { data } = await apiClient.patch(`/adressen/${id}`, updates);
    return data;
  },

  getHaeuser: async (adresseId: number): Promise<Haus[]> => {
    const { data } = await apiClient.get(`/adressen/${adresseId}/haeuser`);
    return data;
  },

  getEinheiten: async (hausId: number): Promise<Einheit[]> => {
    const { data } = await apiClient.get(`/haeuser/${hausId}/einheiten`);
    return data;
  },

  getVerfahrenAtAdresse: async (
    adresseId: number,
  ): Promise<
    {
      id: number;
      aktenzeichen: string | null;
      staat: string;
      einheit_bezeichnung: string | null;
      haus_bezeichnung: string | null;
    }[]
  > => {
    const { data } = await apiClient.get(`/adressen/${adresseId}/verfahren`);
    return data;
  },

  getInserateAtAdresse: async (
    adresseId: number,
  ): Promise<
    {
      id: number;
      title: string;
      platform: string;
      state: string;
      listing_url: string;
      is_online: boolean;
    }[]
  > => {
    const { data } = await apiClient.get(`/adressen/${adresseId}/inserate`);
    return data;
  },

  createHierarchy: async (
    body: HierarchyCreateData,
  ): Promise<HierarchyResponse> => {
    const { data } = await apiClient.post("/adressen/hierarchie", body);
    return data;
  },

  getHistory: async (id: number): Promise<AuditLogEntry[]> => {
    const { data } = await apiClient.get(`/adressen/${id}/verlauf`);
    return data;
  },
};
